import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Table, Button, Space, message, Tag, Card, Row, Col, Select, Tooltip, Badge, Modal } from 'antd';
import { PlusOutlined, EditOutlined, EyeOutlined, CheckCircleOutlined, CloseCircleOutlined, ClockCircleOutlined, UnorderedListOutlined, CalendarOutlined, FormOutlined, LockOutlined, UnlockOutlined, PrinterOutlined, FilePdfOutlined, FileExcelOutlined } from '@ant-design/icons';
import apiClient from '../../../../api/apiClient';
import { getCicloAcademico, getAniosAcademicos } from '../../../../utils/cicloEscolar';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import CrearEditarActividadModal from './components/CrearEditarActividadModal';
import VerActividadModal from './components/VerActividadModal';
import CerrarUnidadModal from './components/CerrarUnidadModal';
import SolicitarReaperturaModal from './components/SolicitarReaperturaModal';
import CalificarActividadModal from './components/CalificarActividadModal';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';

dayjs.extend(customParseFormat);

const { Option } = Select;

const Actividades = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [asignaciones, setAsignaciones] = useState([]);
  const [unidades, setUnidades] = useState([]);
  const [actividades, setActividades] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingUnidades, setLoadingUnidades] = useState(false);
  const [loadingActividades, setLoadingActividades] = useState(false);

  // Obtener usuario logueado
  const user = JSON.parse(localStorage.getItem('user')) || { rol: null, IdUsuario: null };
  const esDocente = user.rol === 4;
  const esAlumno = user.rol === 5;

  // Estado para el idDocente del usuario logueado (si es docente)
  const [idDocenteLogueado, setIdDocenteLogueado] = useState(null);

  // Estado para verificar si hay solicitud de reapertura pendiente o aprobada
  const [tieneSolicitudPendiente, setTieneSolicitudPendiente] = useState(false);
  const [estadoSolicitud, setEstadoSolicitud] = useState(null); // 'pendiente', 'aprobada', null

  // Estado para exportar/imprimir
  const [modalImprimirVisible, setModalImprimirVisible] = useState(false);
  const [actividadParaImprimir, setActividadParaImprimir] = useState(null);
  const [loadingExportar, setLoadingExportar] = useState(false);

  // Modales
  const [modalCrearEditarVisible, setModalCrearEditarVisible] = useState(false);
  const [modalVerVisible, setModalVerVisible] = useState(false);
  const [modalCerrarUnidadVisible, setModalCerrarUnidadVisible] = useState(false);
  const [modalSolicitarReaperturaVisible, setModalSolicitarReaperturaVisible] = useState(false);
  const [modalCalificarVisible, setModalCalificarVisible] = useState(false);
  const [actividadSeleccionada, setActividadSeleccionada] = useState(null);
  const [unidadSeleccionada, setUnidadSeleccionada] = useState(null);
  const [asignacionSeleccionada, setAsignacionSeleccionada] = useState(null);
  const [modoEdicion, setModoEdicion] = useState(false);

  // Catálogos para filtros
  const [docentes, setDocentes] = useState([]);
  const [grados, setGrados] = useState([]);
  const [secciones, setSecciones] = useState([]);
  const [jornadas, setJornadas] = useState([]);

  // Filtros
  const [filtros, setFiltros] = useState({
    anio: getCicloAcademico(),
    idGrado: null,
    idSeccion: null,
    idJornada: null,
    idDocente: null
  });

  // Filtro de estado de actividades
  const [filtroEstadoActividad, setFiltroEstadoActividad] = useState('activas'); // 'activas', 'inactivas', 'todas'

  useEffect(() => {
    const inicializar = async () => {
      if (esDocente) {
        await obtenerIdDocente();
      } else if (!esAlumno) {
        cargarCatalogos();
        cargarAsignaciones();
      }
    };
    inicializar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // El docente espera su idDocente: sin él se pedirían las asignaciones de todos
    if (esDocente && !filtros.idDocente) return;
    if (!esAlumno) {
      cargarAsignaciones();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtros.anio, filtros.idGrado, filtros.idSeccion, filtros.idJornada, filtros.idDocente]);

  const obtenerIdDocente = async () => {
    try {
      const response = await apiClient.get('/docentes');
      if (response.data.success) {
        const docentes = response.data.data;
        const miDocente = docentes.find(doc => doc.idUsuario === user.IdUsuario);
        if (miDocente) {
          const idDocente = miDocente.idDocente;
          setIdDocenteLogueado(idDocente);
          setFiltros(prev => ({ ...prev, idDocente: idDocente }));
          cargarCatalogos();
        } else {
          message.error('No se encontró el perfil de docente asociado a tu usuario');
        }
      }
    } catch (error) {
      message.error('Error al obtener información del docente');
    }
  };

  const cargarCatalogos = async () => {
    try {
      const [docentesRes, gradosRes, seccionesRes, jornadasRes] = await Promise.all([
        apiClient.get('/docentes'),
        apiClient.get('/grados'),
        apiClient.get('/secciones'),
        apiClient.get('/jornadas')
      ]);

      if (docentesRes.data.success) setDocentes(docentesRes.data.data);
      if (gradosRes.data.success) setGrados(gradosRes.data.data);
      if (seccionesRes.data.success) setSecciones(seccionesRes.data.data);
      if (jornadasRes.data.success) setJornadas(jornadasRes.data.data);
    } catch (error) {
      message.error('Error al cargar catálogos');
    }
  };

  const cargarAsignaciones = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filtros.anio) params.anio = filtros.anio;
      if (filtros.idGrado) params.idGrado = filtros.idGrado;
      if (filtros.idSeccion) params.idSeccion = filtros.idSeccion;
      if (filtros.idJornada) params.idJornada = filtros.idJornada;
      if (filtros.idDocente) params.idDocente = filtros.idDocente;

      const response = await apiClient.get('/asignaciones', { params });
      let asignacionesData = [];

      if (response.data.success && response.data.data) {
        const data = response.data.data;
        if (Array.isArray(data)) {
          if (data.length > 0 && typeof data[0] === 'object' && !data[0].IdAsignacionDocente) {
            const primerElemento = data[0];
            asignacionesData = Object.keys(primerElemento)
              .filter(key => !isNaN(key))
              .map(key => primerElemento[key])
              .filter(item => item && item.IdAsignacionDocente);
          } else if (data.length > 0 && data[0].IdAsignacionDocente) {
            asignacionesData = data;
          }
        } else if (typeof data === 'object' && data.IdAsignacionDocente) {
          asignacionesData = [data];
        }
      } else if (Array.isArray(response.data)) {
        asignacionesData = response.data;
      }

      setAsignaciones(asignacionesData);
    } catch (error) {
      message.error('Error al cargar asignaciones');
      setAsignaciones([]);
    } finally {
      setLoading(false);
    }
  };

  const cargarUnidadesAsignacion = async (idAsignacion) => {
    setLoadingUnidades(true);
    try {
      const response = await apiClient.get(`/unidades/asignacion/${idAsignacion}`);
      if (response.data.success) {
        setUnidades(response.data.data || []);
      } else {
        setUnidades([]);
        message.warning('No se encontraron unidades para esta asignación');
      }
    } catch (error) {
      message.error('Error al cargar las unidades');
      setUnidades([]);
    } finally {
      setLoadingUnidades(false);
    }
  };

  const cargarActividadesUnidad = async (idUnidad) => {
    setLoadingActividades(true);
    try {
      const response = await apiClient.get(`/actividades/unidad/${idUnidad}`);

      if (response.data.success) {
        const actividadesData = response.data.data || [];
        setActividades(actividadesData);
      } else {
        setActividades([]);
      }
    } catch (error) {
      message.error('Error al cargar las actividades');
      setActividades([]);
    } finally {
      setLoadingActividades(false);
    }
  };

  const handleFiltroChange = (campo, valor) => {
    if (esDocente && campo === 'idDocente') return;
    setFiltros(prev => ({ ...prev, [campo]: valor }));
  };

  const limpiarFiltros = () => {
    setFiltros({
      anio: getCicloAcademico(),
      idGrado: null,
      idSeccion: null,
      idJornada: null,
      idDocente: esDocente ? idDocenteLogueado : null
    });
  };

  const handleSeleccionarAsignacion = async (asignacion) => {
    setAsignacionSeleccionada(asignacion);
    setUnidadSeleccionada(null);
    setActividades([]);
    await cargarUnidadesAsignacion(asignacion.IdAsignacionDocente);
  };

  const verificarSolicitudPendiente = async (idUnidad) => {
    if (!esDocente) {
      setTieneSolicitudPendiente(false);
      setEstadoSolicitud(null);
      return;
    }

    try {
      const response = await apiClient.get('/solicitudes-reapertura/mis-solicitudes');
      if (response.data.success) {
        const solicitudes = response.data.data || [];
        // Buscar solicitud pendiente o aprobada para esta unidad
        const solicitud = solicitudes.find(
          sol => sol.IdUnidad === idUnidad && (sol.Estado === 'pendiente' || sol.Estado === 'aprobada')
        );

        if (solicitud) {
          setTieneSolicitudPendiente(true);
          setEstadoSolicitud(solicitud.Estado);
        } else {
          setTieneSolicitudPendiente(false);
          setEstadoSolicitud(null);
        }
      }
    } catch (error) {
      setTieneSolicitudPendiente(false);
      setEstadoSolicitud(null);
    }
  };

  const handleSeleccionarUnidad = async (unidad) => {
    setUnidadSeleccionada(unidad);
    setTieneSolicitudPendiente(false); // Reset mientras carga
    setEstadoSolicitud(null); // Reset estado
    await cargarActividadesUnidad(unidad.IdUnidad);
    await verificarSolicitudPendiente(unidad.IdUnidad);
  };

  const abrirModalCrear = () => {
    // Validar que la unidad esté activa si es docente
    if (esDocente && unidadSeleccionada && unidadSeleccionada.Activa !== 1) {
      message.warning('Solo puedes crear actividades en la unidad activa');
      return;
    }

    setActividadSeleccionada(null);
    setModoEdicion(false);
    setModalCrearEditarVisible(true);
  };

  const abrirModalEditar = (actividad) => {
    // Validar que la unidad esté activa si es docente
    if (esDocente && unidadSeleccionada && unidadSeleccionada.Activa !== 1) {
      message.warning('Solo puedes editar actividades de la unidad activa');
      return;
    }

    setActividadSeleccionada(actividad);
    setModoEdicion(true);
    setModalCrearEditarVisible(true);
  };

  const abrirModalVer = (actividad) => {
    setActividadSeleccionada(actividad);
    setModalVerVisible(true);
  };

  const irACalificar = (actividad) => {
    setActividadSeleccionada(actividad);
    setModalCalificarVisible(true);
  };

  // Función de eliminar deshabilitada - Ahora se usa el Switch de Estado en el modal de Editar
  /* const handleEliminarActividad = async (idActividad) => {
    // Validar que la unidad esté activa si es docente
    if (esDocente && unidadSeleccionada && unidadSeleccionada.Activa !== 1) {
      message.warning('Solo puedes eliminar actividades de la unidad activa');
      return;
    }

    try {
      const response = await apiClient.delete(`/actividades/${idActividad}`);
      if (response.data.success) {
        message.success('Actividad eliminada correctamente');
        await cargarActividadesUnidad(unidadSeleccionada.IdUnidad);
      }
    } catch (error) {
      message.error(error.response?.data?.error || 'Error al eliminar actividad');
    }
  }; */

  // Filtrar actividades según el estado seleccionado
  const actividadesFiltradas = actividades.filter(actividad => {
    if (filtroEstadoActividad === 'activas') {
      return actividad.Estado === true || actividad.Estado === 1;
    } else if (filtroEstadoActividad === 'inactivas') {
      return actividad.Estado === false || actividad.Estado === 0;
    }
    // 'todas'
    return true;
  });

  const columnasAsignaciones = [
    {
      title: 'Año',
      dataIndex: 'Anio',
      key: 'Anio',
      width: 80,
      render: (anio) => <Tag color="blue">{anio}</Tag>
    },
    {
      title: 'Docente',
      dataIndex: 'NombreDocente',
      key: 'NombreDocente',
      width: 200
    },
    {
      title: 'Curso',
      dataIndex: 'NombreCurso',
      key: 'NombreCurso',
      width: 180
    },
    {
      title: 'Grado',
      dataIndex: 'NombreGrado',
      key: 'NombreGrado',
      width: 150
    },
    {
      title: 'Sección',
      dataIndex: 'NombreSeccion',
      key: 'NombreSeccion',
      width: 100,
      align: 'center'
    },
    {
      title: 'Jornada',
      dataIndex: 'NombreJornada',
      key: 'NombreJornada',
      width: 120
    },
    {
      title: 'Acciones',
      key: 'acciones',
      width: 150,
      fixed: 'right',
      render: (_, record) => (
        <Button
          type="primary"
          size="small"
          icon={<UnorderedListOutlined />}
          onClick={() => handleSeleccionarAsignacion(record)}
        >
          Ver Unidades
        </Button>
      )
    }
  ];

  const columnasUnidades = [
    {
      title: 'Unidad',
      dataIndex: 'NumeroUnidad',
      key: 'NumeroUnidad',
      width: 100,
      render: (num, record) => (
        <Space>
          {record.Activa === 1 && <Badge status="processing" />}
          <strong>Unidad {num}</strong>
        </Space>
      )
    },
    {
      title: 'Nombre',
      dataIndex: 'NombreUnidad',
      key: 'NombreUnidad',
      width: 200
    },
    {
      title: 'Punteo Zona',
      dataIndex: 'PunteoZona',
      key: 'PunteoZona',
      width: 120,
      align: 'center',
      render: (val) => <Tag color="blue">{parseFloat(val)} pts</Tag>
    },
    {
      title: 'Punteo Final',
      dataIndex: 'PunteoFinal',
      key: 'PunteoFinal',
      width: 120,
      align: 'center',
      render: (val) => <Tag color="green">{parseFloat(val)} pts</Tag>
    },
    {
      title: 'Estado',
      dataIndex: 'Activa',
      key: 'Activa',
      width: 120,
      align: 'center',
      render: (activa) => (
        activa === 1 ? (
          <Tag icon={<CheckCircleOutlined />} color="success">Activa</Tag>
        ) : (
          <Tag icon={<CloseCircleOutlined />} color="default">Cerrada</Tag>
        )
      )
    },
    {
      title: 'Acciones',
      key: 'acciones',
      width: 180,
      fixed: 'right',
      render: (_, record) => (
        <Button
          type="primary"
          size="small"
          icon={<CalendarOutlined />}
          onClick={() => handleSeleccionarUnidad(record)}
        >
          Ver Actividades
        </Button>
      )
    }
  ];

  const columnasActividades = [
    {
      title: 'Nombre',
      dataIndex: 'NombreActividad',
      key: 'NombreActividad',
      width: 200
    },
    {
      title: 'Descripción',
      dataIndex: 'Descripcion',
      key: 'Descripcion',
      width: 250,
      ellipsis: true
    },
    {
      title: 'Tipo',
      dataIndex: 'TipoActividad',
      key: 'TipoActividad',
      width: 100,
      align: 'center',
      render: (tipo) => (
        <Tag color={tipo === 'zona' ? 'blue' : 'green'}>
          {tipo === 'zona' ? 'Zona' : 'Final'}
        </Tag>
      )
    },
    {
      title: 'Punteo Máximo',
      dataIndex: 'PunteoMaximo',
      key: 'PunteoMaximo',
      width: 120,
      align: 'center',
      render: (val) => <Tag color="orange">{parseFloat(val)} pts</Tag>
    },
    {
      title: 'Fecha',
      dataIndex: 'FechaActividad',
      key: 'FechaActividad',
      width: 120,
      align: 'center',
      render: (fecha) => fecha ? dayjs(fecha, 'YYYY-MM-DD').format('DD/MM/YYYY') : '-'
    },
    {
      title: 'Calificaciones',
      key: 'Calificaciones',
      width: 140,
      align: 'center',
      render: (_, record) => {
        const calificados = record.AlumnosCalificados || 0;
        const total = record.TotalAlumnos || 0;
        const porcentaje = total > 0 ? Math.round((calificados / total) * 100) : 0;
        const completo = calificados === total && total > 0;

        return (
          <Space direction="vertical" size="small" style={{ width: '100%' }}>
            <Tag
              icon={completo ? <CheckCircleOutlined /> : <ClockCircleOutlined />}
              color={completo ? 'success' : 'warning'}
            >
              {completo ? 'Calificada' : 'Pendiente'}
            </Tag>
            <span style={{ fontSize: '11px', color: '#666' }}>
              {calificados}/{total} ({porcentaje}%)
            </span>
          </Space>
        );
      }
    },
    {
      title: 'Estado',
      dataIndex: 'Estado',
      key: 'Estado',
      width: 100,
      align: 'center',
      render: (estado) => (
        estado ? (
          <Tag icon={<CheckCircleOutlined />} color="success">Activa</Tag>
        ) : (
          <Tag icon={<CloseCircleOutlined />} color="default">Inactiva</Tag>
        )
      )
    },
    {
      title: 'Acciones',
      key: 'acciones',
      width: 300,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small" wrap>
          <Tooltip title="Calificar actividad">
            <Button
              type="primary"
              size="small"
              icon={<FormOutlined />}
              onClick={() => irACalificar(record)}
              disabled={!record.Estado}
            >
              Calificar
            </Button>
          </Tooltip>
          <Tooltip title="Ver detalles">
            <Button
              type="link"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => abrirModalVer(record)}
            >
              Ver
            </Button>
          </Tooltip>
          <Tooltip title={esDocente && unidadSeleccionada?.Activa !== 1 ? 'Solo puedes editar actividades de la unidad activa' : 'Editar actividad'}>
            <Button
              type="link"
              size="small"
              icon={<EditOutlined />}
              onClick={() => abrirModalEditar(record)}
              disabled={esDocente && unidadSeleccionada?.Activa !== 1}
            >
              Editar
            </Button>
          </Tooltip>
          <Tooltip title="Imprimir / Exportar reporte de calificaciones">
            <Button
              type="link"
              size="small"
              icon={<PrinterOutlined />}
              onClick={() => handleAbrirModalImprimir(record)}
            >
              Imprimir
            </Button>
          </Tooltip>
          {/* Botón de Eliminar oculto - Ahora se usa el Switch de Estado en el modal de Editar */}
          {/* <Popconfirm
            title="¿Eliminar actividad?"
            description="Esta acción no se puede deshacer"
            onConfirm={() => handleEliminarActividad(record.IdActividad)}
            okText="Sí, eliminar"
            cancelText="Cancelar"
            okButtonProps={{ danger: true }}
            disabled={esDocente && unidadSeleccionada?.Activa !== 1}
          >
            <Tooltip title={esDocente && unidadSeleccionada?.Activa !== 1 ? 'Solo puedes eliminar actividades de la unidad activa' : 'Eliminar actividad'}>
              <Button
                type="link"
                size="small"
                danger
                icon={<DeleteOutlined />}
                disabled={esDocente && unidadSeleccionada?.Activa !== 1}
              >
                Eliminar
              </Button>
            </Tooltip>
          </Popconfirm> */}
        </Space>
      )
    }
  ];

  // Calcular resumen de punteos (solo actividades activas)
  const calcularResumen = () => {
    if (!actividades.length || !unidadSeleccionada) return null;

    // Solo considerar actividades activas para el resumen
    const actividadesActivas = actividades.filter(a => a.Estado === true || a.Estado === 1);

    const actividadesZona = actividadesActivas.filter(a => a.TipoActividad === 'zona');
    const actividadesFinal = actividadesActivas.filter(a => a.TipoActividad === 'final');

    const sumaZona = actividadesZona.reduce((acc, a) => acc + parseFloat(a.PunteoMaximo || 0), 0);
    const sumaFinal = actividadesFinal.reduce((acc, a) => acc + parseFloat(a.PunteoMaximo || 0), 0);

    const punteoZonaConfig = parseFloat(unidadSeleccionada.PunteoZona);
    const punteoFinalConfig = parseFloat(unidadSeleccionada.PunteoFinal);

    return {
      sumaZona,
      sumaFinal,
      punteoZonaConfig,
      punteoFinalConfig,
      zonaCompleta: sumaZona === punteoZonaConfig,
      finalCompleta: sumaFinal === punteoFinalConfig
    };
  };

  const resumen = calcularResumen();

  // ── Imprimir / Exportar ────────────────────────────────────────────────────

  const handleAbrirModalImprimir = (actividad) => {
    setActividadParaImprimir(actividad);
    setModalImprimirVisible(true);
  };

  const obtenerDatosImprimir = async () => {
    const response = await apiClient.get(`/actividades/${actividadParaImprimir.IdActividad}/alumnos`);
    if (!response.data.success) throw new Error('Error al cargar alumnos');
    const alumnos = response.data.data || [];

    const punteos = alumnos
      .filter(a => a.Punteo !== null && a.Punteo !== undefined)
      .map(a => parseFloat(a.Punteo));

    const stats = punteos.length > 0
      ? {
          promedio: (punteos.reduce((s, p) => s + p, 0) / punteos.length).toFixed(2),
          maximo: Math.max(...punteos).toFixed(2),
          minimo: Math.min(...punteos).toFixed(2),
          calificados: punteos.length,
          total: alumnos.length,
        }
      : { promedio: '—', maximo: '—', minimo: '—', calificados: 0, total: alumnos.length };

    return { alumnos, stats };
  };

  const generarPDF = async () => {
    setLoadingExportar(true);
    try {
      const { alumnos, stats } = await obtenerDatosImprimir();
      const act = actividadParaImprimir;
      const fechaReporte = dayjs().format('DD/MM/YYYY HH:mm');
      const fechaActividad = act.FechaActividad
        ? dayjs(act.FechaActividad, 'YYYY-MM-DD').format('DD/MM/YYYY')
        : '—';

      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      let y = 16;

      // Título
      doc.setFontSize(15);
      doc.setFont('helvetica', 'bold');
      doc.text('REPORTE DE ACTIVIDAD', pageWidth / 2, y, { align: 'center' });
      y += 6;

      doc.setDrawColor(200, 200, 200);
      doc.line(14, y, pageWidth - 14, y);
      y += 7;

      // Info en 2 columnas
      const col1 = 14;
      const col2 = pageWidth / 2 + 2;
      const lblOffset1 = 28;
      const lblOffset2 = 28;

      const infoRows = [
        ['Actividad:', act.NombreActividad, 'Docente:', asignacionSeleccionada?.NombreDocente || '—'],
        ['Tipo:', act.TipoActividad === 'zona' ? 'Zona' : 'Final', 'Curso:', asignacionSeleccionada?.NombreCurso || '—'],
        ['Grado / Sección:', `${asignacionSeleccionada?.NombreGrado || ''} ${asignacionSeleccionada?.NombreSeccion || ''}`.trim(), 'Jornada:', asignacionSeleccionada?.NombreJornada || '—'],
        ['Unidad:', `${unidadSeleccionada?.NumeroUnidad} — ${unidadSeleccionada?.NombreUnidad || ''}`, 'Punteo Máx.:', `${act.PunteoMaximo} pts`],
        ['Fecha actividad:', fechaActividad, 'Fecha del reporte:', fechaReporte],
      ];

      doc.setFontSize(9);
      infoRows.forEach(([lbl1, val1, lbl2, val2]) => {
        doc.setFont('helvetica', 'bold');
        doc.text(lbl1, col1, y);
        doc.setFont('helvetica', 'normal');
        doc.text(String(val1 ?? '—'), col1 + lblOffset1, y);
        if (lbl2) {
          doc.setFont('helvetica', 'bold');
          doc.text(lbl2, col2, y);
          doc.setFont('helvetica', 'normal');
          doc.text(String(val2 ?? '—'), col2 + lblOffset2, y);
        }
        y += 6;
      });

      y += 2;
      doc.line(14, y, pageWidth - 14, y);
      y += 6;

      // Estadísticas en banda horizontal
      doc.setFontSize(8);
      const statsItems = [
        ['Promedio', stats.promedio !== '—' ? `${stats.promedio} pts` : '—'],
        ['Máximo',   stats.maximo  !== '—' ? `${stats.maximo} pts`  : '—'],
        ['Mínimo',   stats.minimo  !== '—' ? `${stats.minimo} pts`  : '—'],
        ['Calificados', `${stats.calificados} / ${stats.total}`],
      ];
      const slotW = (pageWidth - 28) / statsItems.length;
      statsItems.forEach(([label, valor], i) => {
        const cx = 14 + i * slotW + slotW / 2;
        doc.setFont('helvetica', 'bold');
        doc.text(label, cx, y, { align: 'center' });
        doc.setFont('helvetica', 'normal');
        doc.setFontSize(12);
        doc.text(String(valor), cx, y + 7, { align: 'center' });
        doc.setFontSize(8);
      });
      y += 16;

      doc.line(14, y, pageWidth - 14, y);
      y += 4;

      // Tabla de alumnos
      autoTable(doc, {
        startY: y,
        head: [['#', 'Carnet', 'Alumno', 'Punteo', 'Observaciones', 'Estado']],
        body: alumnos.map((alumno, i) => [
          i + 1,
          alumno.IdAlumno,
          `${alumno.Apellidos || ''}, ${alumno.Nombres || ''}`,
          alumno.Punteo !== null && alumno.Punteo !== undefined
            ? parseFloat(alumno.Punteo).toFixed(2)
            : '—',
          alumno.Observaciones || '',
          alumno.IdCalificacion ? 'Calificado' : 'Pendiente',
        ]),
        styles: { fontSize: 8, cellPadding: 2 },
        headStyles: { fillColor: [24, 144, 255], textColor: 255, fontStyle: 'bold' },
        columnStyles: {
          0: { halign: 'center', cellWidth: 10 },
          1: { halign: 'center', cellWidth: 18 },
          2: { cellWidth: 65 },
          3: { halign: 'center', cellWidth: 20 },
          4: { cellWidth: 52 },
          5: { halign: 'center', cellWidth: 22 },
        },
        alternateRowStyles: { fillColor: [245, 247, 250] },
        didParseCell: (data) => {
          if (data.column.index === 5 && data.section === 'body') {
            data.cell.styles.textColor =
              data.cell.text[0] === 'Calificado' ? [40, 140, 40] : [180, 120, 0];
            data.cell.styles.fontStyle = 'bold';
          }
        },
      });

      const nombreArchivo = `Actividad_${act.NombreActividad.replace(/\s+/g, '_')}_${dayjs().format('YYYYMMDD')}.pdf`;
      doc.save(nombreArchivo);
      message.success('PDF generado correctamente');
      setModalImprimirVisible(false);
    } catch (error) {
      message.error('Error al generar el PDF');
    } finally {
      setLoadingExportar(false);
    }
  };

  const exportarExcel = async () => {
    setLoadingExportar(true);
    try {
      const { alumnos, stats } = await obtenerDatosImprimir();
      const act = actividadParaImprimir;
      const fechaReporte = dayjs().format('DD/MM/YYYY HH:mm');
      const fechaActividad = act.FechaActividad
        ? dayjs(act.FechaActividad, 'YYYY-MM-DD').format('DD/MM/YYYY')
        : '—';

      const metaRows = [
        ['REPORTE DE ACTIVIDAD'],
        [],
        ['Actividad:',      act.NombreActividad],
        ['Tipo:',           act.TipoActividad === 'zona' ? 'Zona' : 'Final'],
        ['Docente:',        asignacionSeleccionada?.NombreDocente || '—'],
        ['Curso:',          asignacionSeleccionada?.NombreCurso || '—'],
        ['Grado / Sección:', `${asignacionSeleccionada?.NombreGrado || ''} ${asignacionSeleccionada?.NombreSeccion || ''}`.trim()],
        ['Jornada:',        asignacionSeleccionada?.NombreJornada || '—'],
        ['Unidad:',         `${unidadSeleccionada?.NumeroUnidad} — ${unidadSeleccionada?.NombreUnidad || ''}`],
        ['Punteo Máximo:',  `${act.PunteoMaximo} pts`],
        ['Fecha actividad:', fechaActividad],
        ['Fecha del reporte:', fechaReporte],
        [],
        ['Promedio:', stats.promedio !== '—' ? `${stats.promedio} pts` : '—',
         'Máximo:',   stats.maximo  !== '—' ? `${stats.maximo} pts`  : '—',
         'Mínimo:',   stats.minimo  !== '—' ? `${stats.minimo} pts`  : '—',
         'Calificados:', `${stats.calificados} / ${stats.total}`],
        [],
        ['#', 'Carnet', 'Apellidos', 'Nombres', 'Punteo', 'Observaciones', 'Estado'],
        ...alumnos.map((alumno, i) => [
          i + 1,
          alumno.IdAlumno,
          alumno.Apellidos || '',
          alumno.Nombres  || '',
          alumno.Punteo !== null && alumno.Punteo !== undefined
            ? parseFloat(alumno.Punteo)
            : '',
          alumno.Observaciones || '',
          alumno.IdCalificacion ? 'Calificado' : 'Pendiente',
        ]),
      ];

      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.aoa_to_sheet(metaRows);
      XLSX.utils.book_append_sheet(wb, ws, 'Reporte');
      saveAs(
        new Blob([XLSX.write(wb, { bookType: 'xlsx', type: 'array' })]),
        `Actividad_${act.NombreActividad.replace(/\s+/g, '_')}_${dayjs().format('YYYYMMDD')}.xlsx`
      );
      message.success('Excel exportado correctamente');
      setModalImprimirVisible(false);
    } catch (error) {
      message.error('Error al exportar Excel');
    } finally {
      setLoadingExportar(false);
    }
  };

  return (
    <div style={{ padding: '24px' }}>
      {/* Card de Asignaciones */}
      {!esAlumno && (
        <Card
          title={
            <span>
              <UnorderedListOutlined /> {esDocente ? 'Mis Asignaciones' : 'Seleccione una Asignación'}
            </span>
          }
          style={{ marginBottom: '24px' }}
        >
          {/* Filtros */}
          <Row gutter={[16, 16]} style={{ marginBottom: '16px' }}>
            <Col xs={24} sm={12} md={6} lg={4}>
              <Select
                placeholder="Filtrar por año"
                style={{ width: '100%' }}
                value={filtros.anio}
                onChange={(value) => handleFiltroChange('anio', value)}
                allowClear
              >
                {getAniosAcademicos().map(y => <Option key={y} value={y}>{y}</Option>)}
              </Select>
            </Col>
            <Col xs={24} sm={12} md={6} lg={5}>
              <Select
                placeholder="Filtrar por grado"
                style={{ width: '100%' }}
                value={filtros.idGrado}
                onChange={(value) => handleFiltroChange('idGrado', value)}
                allowClear
                showSearch
                optionFilterProp="children"
              >
                {grados.map(grado => (
                  <Option key={grado.IdGrado} value={grado.IdGrado}>
                    {grado.NombreGrado}
                  </Option>
                ))}
              </Select>
            </Col>
            <Col xs={24} sm={12} md={6} lg={4}>
              <Select
                placeholder="Filtrar por sección"
                style={{ width: '100%' }}
                value={filtros.idSeccion}
                onChange={(value) => handleFiltroChange('idSeccion', value)}
                allowClear
              >
                {secciones.map(seccion => (
                  <Option key={seccion.IdSeccion} value={seccion.IdSeccion}>
                    {seccion.NombreSeccion}
                  </Option>
                ))}
              </Select>
            </Col>
            <Col xs={24} sm={12} md={6} lg={4}>
              <Select
                placeholder="Filtrar por jornada"
                style={{ width: '100%' }}
                value={filtros.idJornada}
                onChange={(value) => handleFiltroChange('idJornada', value)}
                allowClear
              >
                {jornadas.map(jornada => (
                  <Option key={jornada.IdJornada} value={jornada.IdJornada}>
                    {jornada.NombreJornada}
                  </Option>
                ))}
              </Select>
            </Col>
            {!esDocente && (
              <Col xs={24} sm={12} md={6} lg={5}>
                <Select
                  placeholder="Filtrar por docente"
                  style={{ width: '100%' }}
                  value={filtros.idDocente}
                  onChange={(value) => handleFiltroChange('idDocente', value)}
                  allowClear
                  showSearch
                  optionFilterProp="children"
                >
                  {docentes.map(doc => (
                    <Option key={doc.idDocente} value={doc.idDocente}>
                      {doc.NombreDocente}
                    </Option>
                  ))}
                </Select>
              </Col>
            )}
            <Col xs={24} sm={12} md={6} lg={2}>
              <Button onClick={limpiarFiltros} style={{ width: '100%' }}>
                Limpiar
              </Button>
            </Col>
          </Row>

          <Table
            columns={columnasAsignaciones}
            dataSource={asignaciones}
            rowKey="IdAsignacionDocente"
            loading={loading}
            pagination={{
              pageSize: 5,
              showSizeChanger: true,
              showTotal: (total) => `Total: ${total} asignaciones`,
            }}
            bordered
            size="middle"
            scroll={{ x: 1000 }}
          />
        </Card>
      )}

      {/* Card de Unidades */}
      {asignacionSeleccionada && !esAlumno && (
        <Card
          title={
            <span>
              <UnorderedListOutlined /> Unidades de: {asignacionSeleccionada.NombreDocente} - {asignacionSeleccionada.NombreCurso}
            </span>
          }
          style={{ marginBottom: '24px' }}
        >
          <Table
            columns={columnasUnidades}
            dataSource={unidades}
            rowKey="IdUnidad"
            loading={loadingUnidades}
            pagination={false}
            bordered
            size="middle"
            scroll={{ x: 800 }}
          />
        </Card>
      )}

      {/* Card de Actividades */}
      {unidadSeleccionada && (
        <Card
          title={
            <span>
              <CalendarOutlined /> Actividades - Unidad {unidadSeleccionada.NumeroUnidad}: {unidadSeleccionada.NombreUnidad}
            </span>
          }
          extra={
            !esAlumno && (
              <Space>
                {esDocente && unidadSeleccionada?.Activa !== 1 ? (
                  <Tooltip
                    title={
                      tieneSolicitudPendiente
                        ? estadoSolicitud === 'aprobada'
                          ? 'Tu solicitud fue aprobada. El administrador reabrirá la unidad pronto.'
                          : 'Ya existe una solicitud de reapertura pendiente para esta unidad'
                        : 'Solicitar la reapertura de esta unidad al administrador'
                    }
                  >
                    <Button
                      type={estadoSolicitud === 'aprobada' ? 'default' : 'primary'}
                      icon={<UnlockOutlined />}
                      onClick={() => setModalSolicitarReaperturaVisible(true)}
                      disabled={tieneSolicitudPendiente}
                    >
                      {tieneSolicitudPendiente
                        ? estadoSolicitud === 'aprobada'
                          ? 'Solicitud Aprobada'
                          : 'Solicitud Pendiente'
                        : 'Solicitar Reapertura'
                      }
                    </Button>
                  </Tooltip>
                ) : (
                  <>
                    <Tooltip
                      title={
                        esDocente && unidadSeleccionada?.Activa !== 1
                          ? 'Solo puedes crear actividades en la unidad activa'
                          : ''
                      }
                    >
                      <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={abrirModalCrear}
                        disabled={esDocente && unidadSeleccionada?.Activa !== 1}
                      >
                        Nueva Actividad
                      </Button>
                    </Tooltip>
                    {/* Botón Cerrar Unidad oculto - Ahora se usa el sistema de Cierre de Unidades del admin */}
                    {/* {esDocente && unidadSeleccionada?.Activa === 1 && (
                      <Button
                        type="default"
                        danger
                        icon={<LockOutlined />}
                        onClick={() => setModalCerrarUnidadVisible(true)}
                      >
                        Cerrar Unidad
                      </Button>
                    )} */}
                  </>
                )}
              </Space>
            )
          }
        >
          {/* Filtro de estado de actividades */}
          <div style={{ marginBottom: '16px', display: 'flex', alignItems: 'center', gap: '12px' }}>
            <strong>Mostrar:</strong>
            <Select
              value={filtroEstadoActividad}
              onChange={setFiltroEstadoActividad}
              style={{ width: 180 }}
            >
              <Option value="activas">Actividades Activas</Option>
              <Option value="inactivas">Actividades Inactivas</Option>
              <Option value="todas">Todas las Actividades</Option>
            </Select>
            <Tag color="blue">
              {actividadesFiltradas.length} {actividadesFiltradas.length === 1 ? 'actividad' : 'actividades'}
            </Tag>
          </div>

          {/* Resumen de punteos */}
          {resumen && !esAlumno && (
            <div style={{
              marginBottom: '16px',
              padding: '12px',
              backgroundColor: '#f0f2f5',
              borderRadius: '4px'
            }}>
              <Row gutter={[16, 8]}>
                <Col span={12}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <strong>Zona:</strong>
                    <Tag color={resumen.zonaCompleta ? 'success' : 'warning'}>
                      {resumen.sumaZona} / {resumen.punteoZonaConfig} pts
                    </Tag>
                    {resumen.zonaCompleta ? (
                      <CheckCircleOutlined style={{ color: '#52c41a' }} />
                    ) : (
                      <CloseCircleOutlined style={{ color: '#faad14' }} />
                    )}
                  </div>
                </Col>
                <Col span={12}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <strong>Final:</strong>
                    <Tag color={resumen.finalCompleta ? 'success' : 'warning'}>
                      {resumen.sumaFinal} / {resumen.punteoFinalConfig} pts
                    </Tag>
                    {resumen.finalCompleta ? (
                      <CheckCircleOutlined style={{ color: '#52c41a' }} />
                    ) : (
                      <CloseCircleOutlined style={{ color: '#faad14' }} />
                    )}
                  </div>
                </Col>
              </Row>
            </div>
          )}

          <Table
            columns={columnasActividades}
            dataSource={actividadesFiltradas}
            rowKey="IdActividad"
            loading={loadingActividades}
            pagination={{
              pageSize: 10,
              showTotal: (total) => `Total: ${total} actividades`
            }}
            bordered
            size="middle"
            scroll={{ x: 1000 }}
            locale={{
              emptyText: filtroEstadoActividad === 'activas'
                ? 'No hay actividades activas para esta unidad'
                : filtroEstadoActividad === 'inactivas'
                ? 'No hay actividades inactivas para esta unidad'
                : 'No hay actividades creadas para esta unidad'
            }}
          />
        </Card>
      )}

      {/* Modales */}
      <CrearEditarActividadModal
        visible={modalCrearEditarVisible}
        actividad={actividadSeleccionada}
        unidad={unidadSeleccionada}
        modoEdicion={modoEdicion}
        onCancel={() => {
          setModalCrearEditarVisible(false);
          setActividadSeleccionada(null);
        }}
        onSuccess={async () => {
          setModalCrearEditarVisible(false);
          setActividadSeleccionada(null);
          if (unidadSeleccionada) {
            await cargarActividadesUnidad(unidadSeleccionada.IdUnidad);
          }
        }}
      />

      <VerActividadModal
        visible={modalVerVisible}
        actividad={actividadSeleccionada}
        onCancel={() => {
          setModalVerVisible(false);
          setActividadSeleccionada(null);
        }}
      />

      <CerrarUnidadModal
        visible={modalCerrarUnidadVisible}
        unidad={unidadSeleccionada}
        asignacion={asignacionSeleccionada}
        onCancel={() => setModalCerrarUnidadVisible(false)}
        onSuccess={async () => {
          // Recargar unidades para reflejar el cambio de estado
          if (asignacionSeleccionada) {
            await cargarUnidadesAsignacion(asignacionSeleccionada.IdAsignacionDocente);
          }
          // Limpiar la selección de unidad
          setUnidadSeleccionada(null);
          setActividades([]);
        }}
      />

      <SolicitarReaperturaModal
        visible={modalSolicitarReaperturaVisible}
        unidad={unidadSeleccionada}
        asignacion={asignacionSeleccionada}
        idDocente={idDocenteLogueado}
        onCancel={() => setModalSolicitarReaperturaVisible(false)}
        onSuccess={() => {
          message.info('Puedes ver el estado de tu solicitud en "Mis Solicitudes de Reapertura"');
          // Marcar que ahora hay una solicitud pendiente para esta unidad
          setTieneSolicitudPendiente(true);
          setEstadoSolicitud('pendiente');
        }}
      />

      <CalificarActividadModal
        visible={modalCalificarVisible}
        onClose={() => {
          setModalCalificarVisible(false);
          setActividadSeleccionada(null);
        }}
        actividad={actividadSeleccionada}
        unidad={unidadSeleccionada}
        asignacion={asignacionSeleccionada}
        onCalificacionesGuardadas={() => {
          // Recargar actividades para actualizar contadores
          if (unidadSeleccionada) {
            cargarActividadesUnidad(unidadSeleccionada.IdUnidad);
          }
        }}
      />

      {/* Modal: elegir formato de exportación */}
      <Modal
        title={
          <Space>
            <PrinterOutlined />
            {actividadParaImprimir
              ? `Exportar reporte — ${actividadParaImprimir.NombreActividad}`
              : 'Exportar reporte'}
          </Space>
        }
        open={modalImprimirVisible}
        onCancel={() => {
          if (!loadingExportar) setModalImprimirVisible(false);
        }}
        footer={null}
        width={400}
        centered
      >
        <p style={{ marginBottom: 24, color: '#555' }}>
          Seleccioná el formato en el que querés exportar el reporte de calificaciones:
        </p>
        <Space direction="vertical" style={{ width: '100%' }} size="middle">
          <Button
            block
            size="large"
            type="primary"
            icon={<FilePdfOutlined />}
            loading={loadingExportar}
            onClick={generarPDF}
            style={{ height: 56, fontSize: 15 }}
          >
            Descargar PDF
          </Button>
          <Button
            block
            size="large"
            icon={<FileExcelOutlined />}
            loading={loadingExportar}
            onClick={exportarExcel}
            style={{ height: 56, fontSize: 15, borderColor: '#52c41a', color: '#52c41a' }}
          >
            Exportar Excel
          </Button>
        </Space>
      </Modal>

      {/* Botón Regresar al Dashboard */}
      <div style={{ marginTop: 24, textAlign: 'left' }}>
        <Button
          size="large"
          onClick={() => navigate('/dashboard')}
        >
          Regresar al Dashboard
        </Button>
      </div>
    </div>
  );
};

export default Actividades;
